import InputField from "components/fields/InputField";
import { useAuthTokens } from "hooks/useAuthTokens";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  useConfirmSignUpMutation,
  useResendOtpMutation,
  useSignUpMutation,
} from "reduxFiles/api/authApi";
import { ConfirmSignUpRequest, SignUpRequest } from "types/authTypes";
import { useForm } from "react-hook-form";
import { useAppDispatch } from "reduxFiles/hooks";
import { addNotification } from "reduxFiles/slices/notificationSlice";
import { useEffect, useState } from "react";
import OtpInput from "components/otp/OtpInput";
import { formatTime } from "services/helper";
import { motion, AnimatePresence } from "framer-motion";
import Button from "components/button/Button";
import { HiArrowLeft, HiArrowRight } from "react-icons/hi2";

export default function SignUp() {
  const RESEND_TIME = 120;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const saveTokensAndUser = useAuthTokens().saveTokensAndUser;
  const isRtl = document.documentElement.dir === "rtl";

  // apis
  const [registerUser, { isLoading: registerLoading }] = useSignUpMutation();
  const [confirmEmail, { isLoading: confirmLoading }] =
    useConfirmSignUpMutation();
  const [resendOtp, { isLoading: resendLoading }] = useResendOtpMutation();

  // states
  const [step, setStep] = useState<"register" | "confirm">("register");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(RESEND_TIME);

  // timer
  useEffect(() => {
    if (step !== "confirm") return;

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step]);

  useEffect(() => {
    if (step === "confirm") {
      setTimer(RESEND_TIME);
    }
  }, [step]);

  // forms
  const {
    register: registerForm,
    handleSubmit: handleRegisterSubmit,
    formState: { errors: registerErrors },
  } = useForm<SignUpRequest>({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const { handleSubmit: handleConfirmSubmit } = useForm<ConfirmSignUpRequest>({
    mode: "onChange",
  });

  const handleResend = async () => {
    try {
      await resendOtp({ mobile, type: "SIGN_UP" }).unwrap();
      setTimer(RESEND_TIME);
    } catch (err) {
      console.error(err);
    }
  };

  const onRegister = async (data: SignUpRequest) => {
    try {
      await registerUser(data).unwrap();
      setMobile(data.mobile);
      setStep("confirm");
    } catch (error) {
      console.error(error);
    }
  };

  const onConfirm = async (code: string) => {
    if (code.length < 4) {
      dispatch(
        addNotification({
          type: "error",
          message: t("auth.resetPassword.invalidCode"),
        })
      );
      return;
    }

    try {
      const result = await confirmEmail({
        mobile,
        token: code,
      }).unwrap();

      saveTokensAndUser(result);
      dispatch(
        addNotification({
          type: "success",
          message: t("auth.signUp.success"),
        })
      );

      navigate("/company/dashboard");
    } catch (error: any) {
      console.error(error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="flex h-full w-full items-center justify-center"
    >
      <div className="w-full max-w-md px-2 sm:px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="mb-4 sm:mb-6"
        >
          <div className="mb-1 flex items-center gap-2">
            <div className="h-1 w-8 rounded-full bg-gradient-to-r from-brand-500 to-indigo-500" />
            <span className="text-xs font-medium uppercase tracking-wider text-brand-500 dark:text-brand-100">
              {t("auth.signUp.badge")}
            </span>
          </div>
          <h4 className="text-2xl font-bold text-navy-700 dark:text-white sm:text-3xl">
            {t("auth.signUp.title")}
          </h4>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("auth.signUp.description")}
          </p>
        </motion.div>

        {/* Forms Container */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="relative"
        >
          <AnimatePresence mode="wait">
            {step === "register" && (
              <motion.form
                key="register"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 30 }}
                transition={{ duration: 0.4 }}
                onSubmit={handleRegisterSubmit(onRegister)}
                noValidate
                className="space-y-3"
              >
                {/* Fullname & Mobile - Row */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <InputField
                    label={t("auth.fields.fullname")}
                    placeholder={t("auth.placeholders.fullname")}
                    autoFocus
                    id="fullname"
                    variant="auth"
                    extra="mb-0"
                    {...registerForm("fullname", {
                      required: t("validation.required"),
                      minLength: {
                        value: 3,
                        message: t("validation.minLength", { min: 3 }),
                      },
                    })}
                    state={registerErrors?.fullname ? "error" : ""}
                    errorMessage={registerErrors.fullname?.message}
                  />
                  <InputField
                    label={t("auth.fields.mobile")}
                    placeholder={t("auth.placeholders.mobile")}
                    id="mobile"
                    variant="auth"
                    extra="mb-0"
                    {...registerForm("mobile", {
                      required: t("validation.required"),
                      pattern: {
                        value: /^09\d{9}$/,
                        message: t("validation.mobile"),
                      },
                    })}
                    state={registerErrors.mobile ? "error" : ""}
                    errorMessage={registerErrors.mobile?.message}
                  />
                </div>

                {/* Email */}
                <InputField
                  label={t("auth.fields.email")}
                  placeholder={t("auth.placeholders.email")}
                  id="email"
                  type="email"
                  variant="auth"
                  extra="mb-0"
                  {...registerForm("email", {
                    required: t("validation.required"),
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: t("validation.email"),
                    },
                  })}
                  state={registerErrors.email ? "error" : ""}
                  errorMessage={registerErrors.email?.message}
                />

                {/* Password */}
                <InputField
                  label={t("auth.fields.password")}
                  placeholder={t("auth.placeholders.password")}
                  id="password"
                  type="password"
                  variant="auth"
                  extra="mb-0"
                  {...registerForm("password", {
                    required: t("validation.required"),
                    minLength: {
                      value: 8,
                      message: t("validation.minLength", { min: 8 }),
                    },
                  })}
                  state={registerErrors.password ? "error" : ""}
                  errorMessage={registerErrors.password?.message}
                />

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={registerLoading}
                  className="mt-1"
                >
                  {t("auth.signUp.submit")}
                </Button>
              </motion.form>
            )}

            {step === "confirm" && (
              <motion.form
                key="confirm"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4 }}
                onSubmit={handleConfirmSubmit(() => onConfirm(otp))}
                noValidate
                className="space-y-4"
              >
                {/* Back Button */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  startIcon={isRtl ? <HiArrowRight /> : <HiArrowLeft />}
                  onClick={() => setStep("register")}
                  className="w-fit"
                >
                  {t("buttons.back")}
                </Button>

                {/* OTP Input */}
                <div className="space-y-3">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {t("auth.signUp.otpSent", { name: mobile })}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      {t("auth.signUp.enterCode")}
                    </p>
                  </div>

                  <OtpInput
                    length={4}
                    onChange={setOtp}
                    onComplete={(code) => {
                      onConfirm(code);
                    }}
                  />
                </div>

                {/* Resend Timer & Button */}
                <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2 dark:bg-navy-800/50">
                  {timer > 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("auth.signUp.resendIn", {
                        seconds: formatTime(timer),
                      })}
                    </p>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      loading={resendLoading}
                      onClick={handleResend}
                      disabled={resendLoading || timer > 0}
                      className="text-xs"
                    >
                      {t("auth.signUp.resendCode")}
                    </Button>
                  )}
                </div>

                {/* Confirm Button */}
                <Button
                  type="submit"
                  variant="success"
                  size="lg"
                  fullWidth
                  loading={confirmLoading}
                >
                  {t("auth.signUp.submitConfirm")}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-4 flex flex-wrap items-center justify-center gap-1 border-t border-gray-200 pt-4 dark:border-navy-700"
        >
          <span className="text-xs text-gray-700 dark:text-gray-400">
            {t("auth.signUp.alreadyHaveAccount")}
          </span>
          <Link
            to="/auth/sign-in"
            className="text-xs font-medium text-brand-500 transition-all hover:text-brand-600 hover:underline dark:text-brand-100"
          >
            {t("auth.signUp.backToSignIn")}
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
}
